import { FC, ReactElement, useCallback, useEffect } from 'react';

import classNames from 'classnames';
import { useDropzone } from 'react-dropzone';
import { FormattedMessage } from 'react-intl';

import styles from './attachment-upload.module.scss';
import {
  AttachmentPipelineStatus,
  IAttachment,
  IExistingAttachment,
} from './attachment.interface';
import { AttachmentRow } from './AttachmentRow';

import { useAttachmentUpload } from 'hooks';
import { AttachmentRejectionReason, IAttachmentUploadUrlResponse } from 'utils';
import { StatusBadgeVariant } from 'views/components/UI/StatusBadge';
import { Text } from 'views/components/UI/Text';

interface IAttachmentUploadProps {
  label: string;
  hint: string;
  maxFiles?: number;
  requestUploadUrl: (
    fileName: string,
    contentType: string,
  ) => Promise<IAttachmentUploadUrlResponse>;
  deletePendingUpload: (storageKey: string) => Promise<void>;
  onBlockingStateChange: (hasBlockingAttachments: boolean) => void;
  onAttachmentsChange?: (attachments: IAttachment[]) => void;
  existingAttachments?: IExistingAttachment[];
  /**
   * Overrides 'ready' attachments' badge while the parent form's
   * create/update request is in flight ('scanning') or just failed
   * ('rejected') — those two states live at the exercise-submission level,
   * not the per-file upload pipeline.
   */
  submitStatus?: 'scanning' | 'rejected';
}

const STATUS_BADGE_VARIANT: Record<AttachmentPipelineStatus, StatusBadgeVariant> = {
  uploading: 'neutral',
  scanning: 'neutral',
  ready: 'success',
  error: 'danger',
  rejected: 'danger',
};

const STATUS_LABEL_ID: Record<AttachmentPipelineStatus, string> = {
  uploading: 'attachments.status.uploading',
  scanning: 'attachments.status.scanning',
  ready: 'attachments.status.ready',
  error: 'attachments.status.error',
  rejected: 'attachments.status.rejected',
};

const REJECTION_REASON_LABEL_ID: Record<AttachmentRejectionReason, string> = {
  'file-too-large': 'attachments.reason.file-too-large',
  'file-empty': 'attachments.reason.file-empty',
  'duplicate-file': 'attachments.reason.duplicate-file',
};

const AttachmentUpload: FC<IAttachmentUploadProps> = ({
  label,
  hint,
  maxFiles,
  requestUploadUrl,
  deletePendingUpload,
  onBlockingStateChange,
  onAttachmentsChange,
  existingAttachments,
  submitStatus,
}): ReactElement => {
  const { attachments, hasBlockingAttachments, addFiles, removeAttachment } =
    useAttachmentUpload(requestUploadUrl, deletePendingUpload);

  useEffect(() => {
    onBlockingStateChange(hasBlockingAttachments);
  }, [hasBlockingAttachments, onBlockingStateChange]);

  useEffect(() => {
    onAttachmentsChange?.(attachments);
  }, [attachments, onAttachmentsChange]);

  const existingCount = existingAttachments?.length ?? 0;
  const isAtCapacity =
    maxFiles !== undefined && existingCount + attachments.length >= maxFiles;

  const onDrop = useCallback(
    (droppedFiles: File[]): void => {
      const remainingCapacity =
        maxFiles !== undefined
          ? Math.max(0, maxFiles - existingCount - attachments.length)
          : droppedFiles.length;
      addFiles(droppedFiles.slice(0, remainingCapacity));
    },
    [addFiles, attachments.length, existingCount, maxFiles],
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    multiple: true,
    onDrop,
  });

  const dropzoneClassNames = classNames(styles.dropzone, {
    [styles['dropzone--active']]: isDragActive,
  });

  const hasRows = Boolean(existingAttachments?.length) || attachments.length > 0;

  return (
    <div className={styles.wrapper}>
      <span className={styles.label}>
        <FormattedMessage id={label} />
      </span>
      {!isAtCapacity && (
        <div {...getRootProps({ className: dropzoneClassNames })}>
          <input {...getInputProps()} />
          <Text variant="bodySmall" className={styles.hint}>
            <FormattedMessage id={hint} />
          </Text>
        </div>
      )}
      {hasRows && (
        <ul className={styles.list}>
          {existingAttachments?.map(attachment => (
            <AttachmentRow
              key={`existing-${attachment.id}`}
              name={attachment.name}
              size={attachment.size}
              contentType={attachment.contentType}
              badgeVariant="info"
              badgeLabelId="attachments.status.saved"
              requestDownloadUrl={attachment.requestDownloadUrl}
              onRemove={attachment.onRemove}
            />
          ))}
          {attachments.map(attachment => {
            const displayStatus: AttachmentPipelineStatus =
              attachment.status === 'ready' && submitStatus
                ? submitStatus
                : attachment.status;

            return (
              <AttachmentRow
                key={attachment.id}
                name={attachment.name}
                size={attachment.size}
                contentType={attachment.file.type}
                previewFile={attachment.file}
                isPending={displayStatus === 'uploading' || displayStatus === 'scanning'}
                badgeVariant={STATUS_BADGE_VARIANT[displayStatus]}
                badgeLabelId={STATUS_LABEL_ID[displayStatus]}
                rejectionReasonLabelId={
                  attachment.status === 'rejected' && attachment.rejectionReason
                    ? REJECTION_REASON_LABEL_ID[attachment.rejectionReason]
                    : undefined
                }
                onRemove={() => removeAttachment(attachment.id)}
              />
            );
          })}
        </ul>
      )}
    </div>
  );
};

export { AttachmentUpload };
